import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useMemo, useState} from 'react';
import type {
  EquipmentTypeRequest,
  EquipmentTypeResponse,
} from 'src/api/ProactApiClient';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {BackBar} from 'src/app/layout/BackBar';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {SidebarLinkInspections} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {
  useCreateEquipmentTypeMutation,
  useGetEquipmentTypeQuery,
  useUpdateEquipmentTypeMutation,
} from 'src/redux/queries/equipmentType';
import {useGetInspectionTypeSelectOptionsQuery} from 'src/redux/queries/inspectionType';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormFieldV2} from 'src/types/FormV2';
import {
  EquipmentTypeDocumentTitle,
  EquipmentTypeFormMeta,
  MapEquipmentTypeResponseToEquipmentTypeRequest,
} from './equipmentTypeHelpers';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface EquipmentTypeFormProps {
  id?: number;
  mode: 'create' | 'edit';
  /** Set to false when embedded under a page-level BackBar/PageHeader (e.g. a tab panel) to avoid duplicating them. Default: true. */
  showHeader?: boolean;
}

export const EquipmentTypeForm: React.FC<EquipmentTypeFormProps> = ({
  id,
  mode,
  showHeader = true,
}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [isSavePending, setIsSavePending] = useState(false);

  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';

  const {data: fetchedItem, isLoading: isItemLoading} =
    useGetEquipmentTypeQuery(id!, {
      skip: !isEditMode || id === undefined,
    });

  const {data: inspectionTypes, isLoading: isInspectionTypesLoading} =
    useGetInspectionTypeSelectOptionsQuery({
      start: 0,
      length: 10000,
      filters: [],
      filterFns: [],
      sorting: [],
    });

  const [createEquipmentType] = useCreateEquipmentTypeMutation();
  const [updateEquipmentType] = useUpdateEquipmentTypeMutation();

  const item: EquipmentTypeResponse | undefined = fetchedItem;
  const isLoading = (isEditMode && isItemLoading) || isInspectionTypesLoading;

  const formMeta = useMemo<IFormFieldV2[]>(
    () =>
      isInspectionTypesLoading
        ? []
        : EquipmentTypeFormMeta(inspectionTypes?.data ?? []),
    [isInspectionTypesLoading, inspectionTypes]
  );

  const form = useEntitySyncedForm({
    formMeta,
    entity: fetchedItem,
    toFormData: toFormDataV2,
  });

  const handleSave = async () => {
    setIsSavePending(true);

    try {
      const values = form.getFieldValues();
      const model: EquipmentTypeRequest = {
        ...MapEquipmentTypeResponseToEquipmentTypeRequest({
          ...values,
          id: item?.id,
        } as EquipmentTypeResponse),
        inspectionTypeIds: (
          (values.inspectionTypeIds ?? []) as {id: number; label: string}[]
        ).map(option => option.id),
      };

      let response: EquipmentTypeResponse;

      if (isCreateMode) {
        response = await createEquipmentType(model).unwrap();
        setAlert(`${response.name} was successfully created`, 'success');
      } else {
        response = await updateEquipmentType(model).unwrap();
        setAlert(
          `${item?.name ?? 'Equipment Type'} was successfully updated`,
          'success'
        );
      }

      navigate(PathUtility.EquipmentTypesView(response.id), {replace: true});
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id
      ? PathUtility.EquipmentTypesView(id)
      : PathUtility.Inspections('equipment-types')
  );

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const pageTitle = isCreateMode
    ? 'New Equipment Type'
    : item?.name ?? 'Edit Equipment Type';

  return (
    <Stack>
      {showHeader && (
        <>
          <BackBar
            fallbackTo={
              isEditMode && id
                ? PathUtility.EquipmentTypesView(id)
                : PathUtility.Inspections('equipment-types')
            }
            fallbackLabel={
              isEditMode ? 'Back to Equipment Type' : 'Back to Equipment Types'
            }
            breadcrumbs={[
              {
                label: EquipmentTypeDocumentTitle,
                to: PathUtility.Inspections('equipment-types'),
              },
              ...(isEditMode && item
                ? [
                    {
                      label: item.name ?? 'Equipment Type',
                      to: PathUtility.EquipmentTypesView(id!),
                    },
                  ]
                : []),
              {label: isCreateMode ? 'New Equipment Type' : 'Edit'},
            ]}
          />

          <PageHeader>
            <PageHeader.Title icon={SidebarLinkInspections.icon}>
              {pageTitle}
            </PageHeader.Title>

            {isEditMode && (
              <PageHeader.Badges>
                <PageHeader.Badge color="yellow" variant="light">
                  Editing
                </PageHeader.Badge>
              </PageHeader.Badges>
            )}

            <PageHeader.Actions>
              <PageHeader.Action variant="cancel" onClick={handleCancel}>
                Cancel
              </PageHeader.Action>
              <PageHeader.Action
                variant={isCreateMode ? 'create' : 'save'}
                onClick={handleValidateAndSave}
                loading={isSavePending}>
                {isCreateMode ? 'Create Equipment Type' : 'Save Changes'}
              </PageHeader.Action>
            </PageHeader.Actions>
          </PageHeader>
        </>
      )}

      <ProactForm form={form}>
        <ProactFormGrid />
      </ProactForm>

      <Group>
        <PageHeader.Action variant="cancel" onClick={handleCancel}>
          Cancel
        </PageHeader.Action>
        <PageHeader.Action
          variant={isCreateMode ? 'create' : 'save'}
          onClick={handleValidateAndSave}
          loading={isSavePending}>
          {isCreateMode ? 'Create Equipment Type' : 'Save Changes'}
        </PageHeader.Action>
      </Group>
    </Stack>
  );
};
